import { queryOptions } from '@tanstack/react-query';
import {
  fetchActiveAnalytics,
  fetchActivity,
  fetchAdAccounts,
  fetchAdminLLMCosts,
  fetchAnalytics,
  fetchAnalyticsUsage,
  fetchBillingCustomer,
  fetchBillingInvoices,
  fetchBillingState,
  fetchBillingUpcoming,
  fetchBrandAiVisibilityHealth,
  fetchBrandAssets,
  fetchBrandAutopilotRecent,
  fetchBrandBrain,
  fetchBrandCalendar,
  fetchBrandChannelDetail,
  fetchBrandChannels,
  fetchBrandFunnel,
  fetchBrandMemoById,
  fetchBrandMemoList,
  fetchBrandOpenFollowups,
  fetchBrandPulse,
  fetchBrandSeoHealth,
  fetchBrands,
  fetchCampaign,
  fetchCampaignRollup,
  fetchCampaigns,
  fetchChannelTimeline,
  fetchCreativeAssets,
  fetchFeed,
  fetchImportedCampaign,
  fetchImportedCampaigns,
  fetchLatestBrandMemo,
  fetchMagTokenTransactions,
  fetchMarketingAssets,
  fetchMe,
  fetchNotificationPreferences,
  fetchNotifications,
  fetchNotificationUnreadCount,
  fetchProviderCampaigns,
  fetchScrapedPages,
  fetchSlackInstallStatus,
  fetchTrust,
  fetchVisitorEvents,
  getRecipientSettings,
  listBrandInputs,
  type NotificationListParams,
} from './api.js';

/**
 * Centralised React Query options for every fetch this dashboard does.
 *
 * Why this module exists:
 *   - Route loaders call `queryClient.ensureQueryData(...)` so navigations
 *     hit the cache on revisit (instant) and parallelise on first visit.
 *   - Components can pass the same options to `useQuery` / `useSuspenseQuery`
 *     if they ever need to subscribe to live data — query keys stay in sync
 *     because there's exactly one place that defines them.
 *
 * Cache TTL strategy: the global default is `staleTime: 30s` (main.tsx).
 * Resources that change rarely (brand brain, ad accounts) get bumped to
 * a longer TTL here so tab-switching feels instant. Anything Mag
 * actively writes (feed, campaigns) inherits the 30s default so we don't
 * show stale state after the user takes an action.
 */

export const meQuery = () =>
  queryOptions({
    queryKey: ['me'] as const,
    queryFn: fetchMe,
  });

export const brandBrainQuery = (brandId: string | null) =>
  queryOptions({
    queryKey: ['brand-brain', brandId] as const,
    queryFn: () => fetchBrandBrain(brandId),
    staleTime: 60_000,
  });

// Brand asset library (M1 s156) — founder-uploaded raw material. The
// settings gallery is the primary consumer; mag's medium router will
// read it too once the creative pipeline lands (s159).
export const brandAssetsQuery = (brandId: string) =>
  queryOptions({
    queryKey: ['brand-assets', brandId] as const,
    queryFn: () => fetchBrandAssets(brandId),
    staleTime: 30_000,
  });

// [12.2L-150] M1 — marketing assets (creative pool) gallery. Sibling
// of brand-assets but a separate bag — see the AI strategist meta arc (CommitMind).
export const marketingAssetsQuery = (
  brandId: string,
  filters?: { includeArchived?: boolean; includeExpired?: boolean },
) =>
  queryOptions({
    queryKey: [
      'marketing-assets',
      brandId,
      filters?.includeArchived ?? false,
      filters?.includeExpired ?? false,
    ] as const,
    queryFn: () => fetchMarketingAssets(brandId, filters),
    staleTime: 30_000,
  });

// Drafted creatives for the active brand (M1 s159) — the gallery on
// /creative. Polls every 4s while there are pending / rendering
// rows so the founder watches them flip to ready without manually
// refreshing.
export const creativeAssetsQuery = (brandId: string) =>
  queryOptions({
    queryKey: ['creative-assets', brandId] as const,
    queryFn: () => fetchCreativeAssets(brandId),
    staleTime: 4_000,
  });

// Per-campaign creative drafts (M1 s159.3) — the gallery on the
// campaign detail page. Same shape as the brand-wide query but
// scoped via the api's ?campaign filter so each campaign claims
// its own drafts.
export const campaignCreativeAssetsQuery = (brandId: string, campaignId: string) =>
  queryOptions({
    queryKey: ['creative-assets', brandId, campaignId] as const,
    queryFn: () => fetchCreativeAssets(brandId, campaignId),
    staleTime: 4_000,
  });

// Scraped landing-site pages (M1 s159.5) — what mag has read off the
// marketing site. Powers the "site mag has read" section in /settings.
// Mutated via the crawl + remove endpoints; we invalidate manually
// from those handlers.
export const scrapedPagesQuery = (brandId: string | null) =>
  queryOptions({
    queryKey: ['scraped-pages', brandId] as const,
    queryFn: () => fetchScrapedPages(brandId),
    staleTime: 60_000,
  });

export const feedQuery = (brandId: string | null) =>
  queryOptions({
    queryKey: ['feed', brandId] as const,
    queryFn: () => fetchFeed(brandId),
    // Mag writes cards on her own schedule, so the feed is the one query
    // where "nothing changed on screen" is a lie rather than a saving. A
    // card landed at 3:45am and the founder found it only by reloading:
    // staleTime alone never refetches, it only decides whether the NEXT
    // mount/focus/reconnect goes to the network, and a tab left open
    // overnight fires none of those.
    //
    // Not in the background: an unfocused tab polling every minute costs
    // the founder's battery and our api nothing useful, because the moment
    // they look the focus refetch fires anyway.
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });

export const activityQuery = (days: number) =>
  queryOptions({
    queryKey: ['activity', days] as const,
    queryFn: () => fetchActivity(days),
  });

// s180 — admin LLM-costs dashboard. Refetches on demand only (no
// pollInterval) — the founder opens this manually a few times a week,
// not as a live monitor. retry: false so a 403 (non-admin) doesn't
// flap; the dashboard shows the "not authorized" view directly.
export const adminLLMCostsQuery = (days: 1 | 7 | 30) =>
  queryOptions({
    queryKey: ['admin-llm-costs', days] as const,
    queryFn: () => fetchAdminLLMCosts(days),
    retry: false,
    staleTime: 60_000,
  });

export const trustQuery = (days: number) =>
  queryOptions({
    queryKey: ['trust', days] as const,
    queryFn: () => fetchTrust(days),
  });

export const campaignsQuery = (brandId: string | null) =>
  queryOptions({
    queryKey: ['campaigns', brandId] as const,
    queryFn: () => fetchCampaigns(brandId),
  });

export const brandAutopilotRecentQuery = (brandId: string) =>
  queryOptions({
    queryKey: ['brand-autopilot-recent', brandId] as const,
    queryFn: () => fetchBrandAutopilotRecent(brandId),
    // Receipts can be stale by a minute — autopilot ticks aren't
    // sub-second and the founder isn't refreshing the home for fresh
    // audit rows. 60s is fine.
    staleTime: 60_000,
  });

export const campaignQuery = (id: string) =>
  queryOptions({
    queryKey: ['campaign', id] as const,
    queryFn: () => fetchCampaign(id),
  });

export const campaignRollupQuery = (id: string) =>
  queryOptions({
    queryKey: ['campaign-rollup', id] as const,
    queryFn: () => fetchCampaignRollup(id),
    // The rollup cache is refreshed by the BullMQ worker; the
    // dashboard shouldn't refetch on every render. 60s feels right
    // for a metrics surface — fresh enough that founder navigation
    // sees updates, slow enough that nothing thrashes.
    staleTime: 60_000,
  });

export const brandOpenFollowupsQuery = (brandId: string) =>
  queryOptions({
    queryKey: ['followups', brandId, 'open'] as const,
    queryFn: () => fetchBrandOpenFollowups(brandId),
  });

export const adAccountsQuery = (brandId: string | null) =>
  queryOptions({
    queryKey: ['ad-accounts', brandId] as const,
    queryFn: () => fetchAdAccounts(brandId),
    staleTime: 60_000,
  });

export const providerCampaignsQuery = (adAccountId: string) =>
  queryOptions({
    queryKey: ['provider-campaigns', adAccountId] as const,
    queryFn: () => fetchProviderCampaigns(adAccountId),
  });

export const importedCampaignsQuery = () =>
  queryOptions({
    queryKey: ['imported-campaigns'] as const,
    queryFn: fetchImportedCampaigns,
  });

export const importedCampaignQuery = (id: string) =>
  queryOptions({
    queryKey: ['imported-campaign', id] as const,
    queryFn: () => fetchImportedCampaign(id),
  });

export const brandsQuery = () =>
  queryOptions({
    queryKey: ['brands'] as const,
    queryFn: fetchBrands,
    // Brand list changes only when the founder explicitly creates/renames
    // one (Phase 3+) — a long TTL keeps the chip render instant after
    // first paint.
    staleTime: 5 * 60_000,
  });

export const analyticsQuery = (brandId: string | null) =>
  queryOptions({
    queryKey: ['analytics', brandId] as const,
    queryFn: () => fetchAnalytics(brandId),
    // Short staleTime so any return-to-page repaints with fresh signals.
    // The /analytics route itself drives ongoing polling via refetchInterval
    // (tight cadence while waiting for the first event, slower once live).
    staleTime: 5_000,
  });

// Active sessions feed — what the dashboard polls every ~15s to keep
// the "who's on the site right now" list moving. Kept separate from
// analyticsQuery so the lightweight active list refreshes without
// having to round-trip the heavier envelope (summary, visitsByDay,
// events) on every poll.
export const activeAnalyticsQuery = (brandId: string) =>
  queryOptions({
    queryKey: ['analytics-active', brandId] as const,
    queryFn: () => fetchActiveAnalytics(brandId),
    staleTime: 5_000,
  });

// Visitor drilldown (s130) — recent events for one anon_id (the live
// "session" row on the dashboard is now per-person, not per-browser-
// session, so the tail shows the whole journey across subdomains).
// Polled at ~3s by the slide-over so the founder watches events tick in.
// Home right-rail pulse (s147) — live activity + cost rollup. Polls
// at 30s by useQuery on /home; staleTime keeps repeated nav cheap.
export const homePulseQuery = (brandId: string | null) =>
  queryOptions({
    queryKey: ['home-pulse', brandId] as const,
    queryFn: () => fetchBrandPulse(brandId),
    staleTime: 20_000,
  });

// Home right-rail "site health" widget (s219) — persisted Lighthouse scores.
// Audits change slowly, so cache longer than the pulse heartbeat.
export const siteHealthQuery = (brandId: string | null) =>
  queryOptions({
    queryKey: ['site-health', brandId] as const,
    queryFn: () => fetchBrandSeoHealth(brandId),
    staleTime: 60_000,
  });

// The same widget's "ai search" section — persisted AI-visibility snapshot.
// Separate query from siteHealthQuery because the two checks run on their own
// schedules and have their own re-run cooldowns; one cache key would make a
// re-run of either look like a refresh of both.
export const aiVisibilityHealthQuery = (brandId: string | null) =>
  queryOptions({
    queryKey: ['ai-visibility-health', brandId] as const,
    queryFn: () => fetchBrandAiVisibilityHealth(brandId),
    staleTime: 60_000,
  });

// Brand-level unified 14-day calendar (agency-coordination C4).
// Aggregates organic slots + paid launches + monthly intent banner
// into a single grid. Slower-changing than feed/pulse; cache 60s.
export const brandCalendarQuery = (brandId: string) =>
  queryOptions({
    queryKey: ['brand-calendar', brandId] as const,
    queryFn: () => fetchBrandCalendar(brandId),
    staleTime: 60_000,
  });

export const brandFunnelQuery = (brandId: string, campaignId?: string | null) =>
  queryOptions({
    queryKey: ['brand-funnel', brandId, campaignId ?? null] as const,
    queryFn: () => fetchBrandFunnel(brandId, campaignId),
    staleTime: 60_000,
  });

// Channel timeline (spec §0a.9 lane 1) — past + future events
// across every channel mag operates. The new home page consumes this
// + brandInputsQuery to render its two-lane layout.
export const channelTimelineQuery = (brandId: string) =>
  queryOptions({
    queryKey: ['channel-timeline', brandId] as const,
    queryFn: () => fetchChannelTimeline(brandId),
    staleTime: 20_000,
  });

// Brand inputs (spec §0a.9 lane 2) — recent founder→mag composer
// rows. The home page lists newest-first; the composer appends in
// place after success.
export const brandInputsQuery = (brandId: string) =>
  queryOptions({
    queryKey: ['brand-inputs', brandId] as const,
    queryFn: () => listBrandInputs(brandId, { limit: 20 }),
    staleTime: 10_000,
  });

// Latest brand memo (spec §0a.3) — the canonical weekly artifact.
// Drives the pinned card on /home. Null when the brand hasn't had
// its first memo yet; renderer shows the "memo coming monday" stub.
export const latestBrandMemoQuery = (brandId: string) =>
  queryOptions({
    queryKey: ['brand-memo', 'latest', brandId] as const,
    queryFn: () => fetchLatestBrandMemo(brandId),
    staleTime: 60_000,
  });

// Memo archive list — compact rows for /brand/$slug/memos.
export const brandMemoListQuery = (brandId: string) =>
  queryOptions({
    queryKey: ['brand-memo', 'list', brandId] as const,
    queryFn: () => fetchBrandMemoList(brandId),
    staleTime: 60_000,
  });

// Brand channels (spec §0b) — list + detail.
export const brandChannelsQuery = (brandId: string) =>
  queryOptions({
    queryKey: ['brand-channels', brandId] as const,
    queryFn: () => fetchBrandChannels(brandId),
    staleTime: 30_000,
  });

export const brandChannelDetailQuery = (
  brandId: string,
  kind: 'instagram' | 'linkedin' | 'tiktok' | 'youtube',
) =>
  queryOptions({
    queryKey: ['brand-channel', brandId, kind] as const,
    queryFn: () => fetchBrandChannelDetail(brandId, kind),
    staleTime: 20_000,
  });

// Full memo by id — for /brand/$slug/memos/$memoId.
export const brandMemoByIdQuery = (brandId: string, memoId: string) =>
  queryOptions({
    queryKey: ['brand-memo', 'by-id', brandId, memoId] as const,
    queryFn: () => fetchBrandMemoById(brandId, memoId),
    // Memos are frozen — once we've fetched a memo, no need to
    // revalidate during a session.
    staleTime: 60 * 60_000,
  });

export const visitorEventsQuery = (brandId: string, anonId: string) =>
  queryOptions({
    queryKey: ['analytics-visitor', brandId, anonId] as const,
    queryFn: () => fetchVisitorEvents(brandId, anonId),
    staleTime: 2_000,
  });

// Notifications (s178) — inbox + bell badge. The stream subscription
// (see lib/notifications-stream.ts) invalidates these on each incoming
// event so the bell + open inbox views stay in sync without polling.

export const notificationUnreadCountQuery = (brand?: string | 'all') =>
  queryOptions({
    queryKey: ['notifications', 'unread-count', brand ?? 'all'] as const,
    queryFn: () => fetchNotificationUnreadCount(brand),
    // The SSE stream pushes invalidations, but a 60s fallback covers
    // the period before the stream subscribes (post-login) and any
    // missed events if the connection drops.
    staleTime: 60_000,
  });

export const notificationsListQuery = (params: NotificationListParams = {}) =>
  queryOptions({
    queryKey: ['notifications', 'list', params] as const,
    queryFn: () => fetchNotifications(params),
    staleTime: 30_000,
  });

export const notificationPreferencesQuery = () =>
  queryOptions({
    queryKey: ['notifications', 'preferences'] as const,
    queryFn: () => fetchNotificationPreferences(),
    staleTime: 60_000,
  });

// s212 — per (user, brand) preferred channel for outbound pings.
// Cached generously since changes go through the mutation which
// invalidates explicitly; the value drives the home page widget +
// is read on every notification dispatch.
export const recipientSettingsQuery = (brandId: string) =>
  queryOptions({
    queryKey: ['notifications', 'recipient-settings', brandId] as const,
    queryFn: () => getRecipientSettings(brandId),
    staleTime: 60_000,
  });

export const slackInstallQuery = (brandId: string) =>
  queryOptions({
    queryKey: ['slack-install', brandId] as const,
    queryFn: () => fetchSlackInstallStatus(brandId),
    // Install rarely changes — long staleTime. Mutations (connect /
    // disconnect) invalidate this key explicitly.
    staleTime: 5 * 60_000,
  });

/** s120 — current org's billing state: plan, subscription status, period
 *  end, and the list of plans the founder can switch to. Drives the
 *  /settings/billing surface + the upgrade prompt anywhere a plan
 *  limit fires. Refreshed after every checkout return + every webhook
 *  the backend processes (via refreshQueries in the post-checkout
 *  handler — see /settings/billing route). */
export const billingQuery = () =>
  queryOptions({
    queryKey: ['billing'] as const,
    queryFn: fetchBillingState,
    // Slightly stale-tolerant — the dashboard re-fetches on focus and
    // post-checkout return anyway. Long-poll-style cadence isn't right
    // for a subscription state that only changes via human action.
    staleTime: 30_000,
  });

/** Stripe customer details (address, tax IDs, default payment method).
 *  Returns null when the org hasn't gone through Checkout yet. */
export const billingCustomerQuery = () =>
  queryOptions({
    queryKey: ['billing', 'customer'] as const,
    queryFn: fetchBillingCustomer,
    staleTime: 60_000,
  });

/** Past invoices, most recent first. Empty array for pre-Stripe orgs. */
export const billingInvoicesQuery = () =>
  queryOptions({
    queryKey: ['billing', 'invoices'] as const,
    queryFn: fetchBillingInvoices,
    staleTime: 60_000,
  });

/** Preview of the next invoice. Null for canceled / pre-Stripe orgs. */
export const billingUpcomingQuery = () =>
  queryOptions({
    queryKey: ['billing', 'upcoming'] as const,
    queryFn: fetchBillingUpcoming,
    staleTime: 60_000,
  });

/** [s264] Mag-token ledger — most recent 30 rows. Read by the
 *  /settings/billing transactions panel. Short staleTime so a top-up
 *  the founder just made shows up the next time they open the page;
 *  the post-checkout refresh invalidates ['billing'] which is a
 *  prefix of this key. */
export const magTokenTransactionsQuery = () =>
  queryOptions({
    queryKey: ['billing', 'mag-token-transactions'] as const,
    queryFn: fetchMagTokenTransactions,
    staleTime: 15_000,
  });

/** s210 — current-cycle analytics event usage. The /settings/billing
 *  usage strip + the /brand/$slug/analytics capacity card both read
 *  this. Shorter staleTime since events flow constantly — a 15s
 *  cache is the sweet spot between "feels live" and "doesn't hammer
 *  the api with every focus." */
export const analyticsUsageQuery = () =>
  queryOptions({
    queryKey: ['billing', 'analytics-usage'] as const,
    queryFn: fetchAnalyticsUsage,
    staleTime: 15_000,
  });
